import { SredProjectSortFields } from '@neo.tax/shared/graphql';
import { SredProject } from '@neo.tax/sred/hooks/useSredProjects';

export type SredDocumentationStatus = {
  systematicInvestigation: boolean;
  technologicalAdvancement: boolean;
  technologicalUncertainty: boolean;
  supportingWork: boolean;
};

const sredDocumentationTitles: Record<keyof SredDocumentationStatus, string> = {
  systematicInvestigation: 'Systematic Investigation',
  technologicalAdvancement: 'Technological Advancement',
  technologicalUncertainty: 'Technological Uncertainty',
  supportingWork: 'Supporting Work',
};

export const hasSystematicInvestigation = (project: SredProject): boolean => !!project.systematicInvestigation;

export const hasTechnologicalAdvancement = (project: SredProject): boolean => !!project.technologicalAdvancement;

export const hasTechnologicalUncertainty = (project: SredProject): boolean => !!project.technologicalUncertainty;

export const hasSupportingWork = (project: SredProject): boolean => !!project.supportingWork;

export const hasAllDocumentation = (project: SredProject): boolean =>
  hasSystematicInvestigation(project) &&
  hasTechnologicalAdvancement(project) &&
  hasTechnologicalUncertainty(project) &&
  hasSupportingWork(project);

export const getSredDocumentationStatus = (project: SredProject): SredDocumentationStatus => ({
  systematicInvestigation: hasSystematicInvestigation(project),
  technologicalAdvancement: hasTechnologicalAdvancement(project),
  technologicalUncertainty: hasTechnologicalUncertainty(project),
  supportingWork: hasSupportingWork(project),
});

export const makeSredDetailsDescription = (sredDocumentationStatus: SredDocumentationStatus): string => {
  const entries: [keyof SredDocumentationStatus, boolean][] = Object.entries(sredDocumentationStatus) as [
    keyof SredDocumentationStatus,
    boolean,
  ][];
  const missingDocumentation = entries.filter(([, hasDoc]) => !hasDoc).map(([key]) => sredDocumentationTitles[key]);
  return `Project is missing the following supporting documentation: ${missingDocumentation.join(', ')}.`;
};

export const mapStringToSortField = (fieldName: string): SredProjectSortFields => {
  switch (fieldName) {
    case 'name':
      return SredProjectSortFields.Name;
    case 'projectOwnerName':
      return SredProjectSortFields.ProjectOwnerName;
    case 'productName':
      return SredProjectSortFields.ProductName;
    case 'ticketCount':
      return SredProjectSortFields.TicketCount;
    case 'selectedCategory':
      return SredProjectSortFields.SelectedCategory;
    case 'sredDocumentationStatus':
      return SredProjectSortFields.SredDocumentationStatus;
    case 'workstreamNamesSearch':
      return SredProjectSortFields.WorkstreamNamesSearch;
    case 'aiGuidance':
      return SredProjectSortFields.AiGuidance;
    default:
      return SredProjectSortFields.Name;
  }
};
